import { useQuery } from '@tanstack/react-query';
import { ReactFlowProvider } from '@xyflow/react';
import { t } from 'i18next';
import { useParams } from 'react-router-dom';

import { AdminBuilderHeader } from '@/app/builder/admin-builder-header';
import { BuilderPage } from '@/app/builder';
import { BuilderStateProvider } from '@/app/builder/builder-state-provider';
import { LoadingSpinner } from '@/components/ui/spinner';
import { adminApi } from '@/features/internal-admin/lib/admin-api';
import { useAdminConsentGate } from '@/features/internal-admin/lib/use-admin-consent-gate';
import { PopulatedFlow } from '@activepieces/shared';

const AdminFlowViewerPage = () => {
  const { tenantId, flowId } = useParams();
  const { canLoadCustomerData, isClientError, consentDialog } =
    useAdminConsentGate(tenantId);

  const { data: flow, isError } = useQuery<PopulatedFlow, Error>({
    queryKey: ['admin-flow', tenantId, flowId],
    queryFn: () => adminApi.getFlow(tenantId!, flowId!),
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    enabled: canLoadCustomerData && !!flowId,
  });

  if (consentDialog) {
    return consentDialog;
  }

  if (isClientError || isError) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('Flow not found')}</p>
      </div>
    );
  }

  if (!flow) {
    return (
      <div className="bg-background flex h-screen w-screen items-center justify-center">
        <LoadingSpinner size={50} />
      </div>
    );
  }

  return (
    <ReactFlowProvider>
      <BuilderStateProvider
        flow={flow}
        flowVersion={flow.version}
        readonly={true}
        run={null}
        canExitRun={false}
        sampleData={{}}
      >
        <BuilderPage
          customHeader={
            <AdminBuilderHeader flowName={flow.version.displayName} />
          }
        />
      </BuilderStateProvider>
    </ReactFlowProvider>
  );
};

export default AdminFlowViewerPage;
